import React, { useState, useCallback, memo, useEffect } from 'react';
import { getBezierPath, EdgeProps, useReactFlow } from '@xyflow/react';
import { useCanvasStore } from '@/stores/canvasStore';
import { useEdgeActiveStore } from '@/stores/edgeActiveStore';
import { setParticlePath, removeParticlePath } from './EdgeParticlesCanvas';

// 删除按钮独立组件：只有它订阅 zoom，避免所有边都因 zoom 变化重渲染
const EdgeDeleteButton: React.FC<{
  x: number;
  y: number;
  onDelete: (e: React.MouseEvent) => void;
}> = ({ x, y, onDelete }) => {
  const zoom = useCanvasStore((s) => s.zoom);
  const invScale = 1 / zoom;
  return (
    <g
      className="edge-delete-btn"
      style={{ cursor: 'pointer', transform: `translate(${x}px, ${y}px) scale(${invScale})`, transformOrigin: '0 0' }}
      onClick={onDelete}
    >
      <circle cx={0} cy={0} r={12} fill="#ef4444" stroke="#ffffff" strokeWidth={2} />
      <line x1={-5} y1={-5} x2={5} y2={5} stroke="#ffffff" strokeWidth={2} strokeLinecap="round" />
      <line x1={5} y1={-5} x2={-5} y2={5} stroke="#ffffff" strokeWidth={2} strokeLinecap="round" />
    </g>
  );
};

const CustomEdgeComponent: React.FC<EdgeProps> = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
}) => {
  const { setEdges, screenToFlowPosition } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  // 删除按钮的显示位置（flow 坐标系）
  const [deletePos, setDeletePos] = useState<{ x: number; y: number } | null>(null);

  // P0-2: 精确选择器，只有自己 isActive 变化才重渲染
  const isActive = useEdgeActiveStore(useCallback((state) => state.activeEdgeIds.has(id), [id]));

  const edgeDeselectCounter = useCanvasStore((s) => s.edgeDeselectCounter);

  useEffect(() => {
    setIsSelected(false);
    setIsHovered(false);
    setDeletePos(null);
  }, [edgeDeselectCounter]);

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const showHighlight = isHovered || isSelected;
  const showParticles = isActive && !showHighlight;

  // 注册/更新路径到 Canvas 粒子注册表
  // 用 useEffect（异步）而非 useLayoutEffect（同步），避免阻塞浏览器绘制
  // useLayoutEffect 在 React commit 阶段同步执行，多个 CustomEdge 同时重渲染时
  // 多个 useLayoutEffect 同步执行会阻塞 rAF → 光流卡顿
  // useEffect 在浏览器绘制后异步执行，不阻塞 rAF
  // 无 cleanup：edgePath 变化时只 update（setParticlePath 内部处理），不 remove + set
  useEffect(() => {
    if (showParticles) {
      setParticlePath(id, edgePath);
    } else {
      removeParticlePath(id);
    }
  }, [id, edgePath, showParticles]);

  // 组件 unmount 时 remove 路径（独立 effect，避免 edgePath 变化时触发 remove）
  useEffect(() => {
    return () => { removeParticlePath(id); };
  }, [id]);

  const handleEdgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // 将屏幕坐标转换为 flow 画布坐标，删除按钮显示在点击位置
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    setDeletePos(flowPos);
    setIsSelected(true);
  }, [screenToFlowPosition]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isSelected) {
      setIsHovered(false);
    }
  }, [isSelected]);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
    setIsSelected(false);
    setDeletePos(null);
  }, [id, setEdges]);

  // 删除按钮位置：优先用点击位置，无则回退到中点
  const btnX = deletePos ? deletePos.x : (sourceX + targetX) / 2;
  const btnY = deletePos ? deletePos.y : (sourceY + targetY) / 2;

  return (
    <g className="react-flow__edge" style={style}>
      {/* 可见 path：stroke-width 由 index.html 的 CSS 规则通过 calc(var(--flow-zoom)) 计算
          屏幕线宽恒定（baseWidth），避免低缩放断线和缩放闪烁
          通过 highlight class 区分线宽 */}
      <path
        d={edgePath}
        className={`react-flow__edge-path${showHighlight ? ' highlight' : ''}`}
        style={{
          stroke: '#ffffff',
          fill: 'none',
          strokeOpacity: showHighlight ? 1 : 0.4,
          pointerEvents: 'none',
        }}
      />
      {/* 不可见的宽 path 作为命中区域：让细线更容易点击
          strokeWidth 用 calc 适配缩放，保证屏幕命中宽度恒定（inline style 在 transparent stroke 下无重绘问题） */}
      <path
        d={edgePath}
        style={{
          stroke: 'transparent',
          strokeWidth: `calc(15 / var(--flow-zoom, 1))`,
          fill: 'none',
          cursor: 'pointer',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleEdgeClick}
      />
      {/* 粒子已移至 Canvas 2D 渲染（EdgeParticlesCanvas），消除 90+ SVG path 的 DOM 开销和 stroke-dashoffset 动画频闪 */}
      {isSelected && (
        <EdgeDeleteButton x={btnX} y={btnY} onDelete={handleDeleteClick} />
      )}
    </g>
  );
};

export const CustomEdge = memo(CustomEdgeComponent);
